import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './styles/index.css';
import './styles/waitlist.css';
import {
  applyEditorToDOM,
  applyThemeToDOM,
  THEME_PRESETS,
  useSettingsStore,
} from './stores/settingsStore';
import type { DesignVariant } from './components/WaitlistLanding';

const WaitlistLanding = lazy(() => import('./components/WaitlistLanding'));

// Determine if we should show the waitlist landing page
function shouldShowWaitlist(): { show: boolean; variant: DesignVariant } {
  const params = new URLSearchParams(window.location.search);
  const hash = window.location.hash;

  // Only show waitlist when explicitly requested via ?landing or #waitlist
  const showLanding = params.has('landing') || hash === '#waitlist';
  if (!showLanding) {
    return { show: false, variant: 'A' };
  }

  const design = params.get('design') as DesignVariant | null;
  const variant: DesignVariant = (design && ['A', 'B', 'C'].includes(design)) ? design : 'A';
  return { show: true, variant };

  return { show: false, variant: 'A' };
}

const waitlistCheck = shouldShowWaitlist();

if (!waitlistCheck.show) {
  // Apply saved theme immediately (before first paint)
  const settings = useSettingsStore.getState().settings;
  const activeTheme = settings.appearance.activeTheme;
  const tempTheme = settings.appearance.tempTheme;

  let colors;
  if (activeTheme === 'temp' && tempTheme) {
    colors = { ...tempTheme.colors, ...settings.appearance.customColors };
  } else {
    const saved = settings.appearance.savedCustomThemes.find((theme) => theme.id === activeTheme);
    const preset = saved ?? THEME_PRESETS.find((theme) => theme.id === activeTheme);
    colors = { ...(preset?.colors ?? THEME_PRESETS[0].colors), ...settings.appearance.customColors };
  }
  applyThemeToDOM(colors);
  applyEditorToDOM(settings.editor);
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  waitlistCheck.show ? (
    <Suspense fallback={<div style={{ background: '#08090f', minHeight: '100vh' }} />}>
      <WaitlistLanding variant={waitlistCheck.variant} />
    </Suspense>
  ) : (
    <App />
  ),
);

window.__diagBoot?.('react render() returned');

// Service worker intentionally disabled in the legacy/projector build.
// (Stale SW caches break the redeploy loop; the inline script in index.html
// unregisters any pre-existing worker and clears caches.)
declare global {
  interface Window { __diagBoot?: (m: string) => void; }
}
window.__diagBoot?.('main.tsx executed');
